import { Link } from 'react-router-dom';
import TrustBadges from '../components/TrustBadges';
import JsonLd from '../components/JsonLd';

const PILLARS = [
  {
    title: 'End-to-end encryption — on by default',
    body: `New transfers are encrypted in your browser with AES-256-GCM before a single byte is uploaded — our servers only ever store ciphertext. The decryption key stays between you and your recipient: it rides the share link's URL fragment (which browsers never send to any server), or is unlocked by the PIN you share out-of-band. Decryption happens on the recipient's device, including the built-in X-ray viewer.`
  },
  {
    title: 'Encryption — in transit and at rest',
    body: `Every upload runs over TLS 1.3 to a US-region server. Files are written to encrypted object storage with AES-256. Database fields containing identifiers are field-level encrypted with rotating keys. There is no point in the lifecycle where your patient files sit in plaintext on a disk we can read.`
  },
  {
    title: 'Extra Security — the details are sealed too',
    body: `File contents are end-to-end encrypted, but a transfer also has a title, a message, file names and a recipient name, and those often carry a patient's name. We seal each of them with glyph encryption layered over AES-256-GCM before it is stored, so the labels around your files are never kept in readable form either.`,
  },
  {
    title: 'Two-factor by design — link + PIN',
    body: `The download link goes to the recipient's email. The 4-digit PIN is communicated out-of-band — you text or call it. An attacker who compromises the recipient's inbox alone cannot open the package. This is HIPAA's "access control" rule, applied to a workflow your staff actually uses.`
  },
  {
    title: 'Auto-expiry and deletion',
    body: `Every transfer expires 7 days after sending. Expired files are removed from object storage and rendered unrecoverable. We never indefinitely retain your patient files. If you need a permanent record, that should live in your PMS — not a transfer service.`
  },
  {
    title: 'No PHI in subject lines or messages',
    body: `Subject lines you set are visible only to you, the sender. Recipients see a generic email — "A secure file is waiting for you" — until they enter the PIN. Patient names and details never leak into mail headers, server logs, or push notifications.`
  },
  {
    title: 'BAA included, automatically',
    body: `Every account accepts our Business Associate Agreement on first sign-in. There is no separate procurement process, no PDF to print and fax. Your BAA is on file, dated, and downloadable from your dashboard at any time.`
  },
  {
    title: 'Audit trail',
    body: `Every transfer keeps an audit log: who sent it, who opened it, when, and from what IP. If you ever need to demonstrate to a covered entity or auditor that a specific file went to a specific recipient, the record exists.`
  },
];

// Exported for scripts/prerender.mjs — see the note in HowItWorks.jsx.
export const SECURITY_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'Security and HIPAA compliance — Intake.Dental Send',
  description: 'How Intake.Dental Send protects dental patient files: end-to-end AES-256 encryption in your browser, two-factor link + PIN access, automatic expiry, BAA included.',
  about: ['HIPAA compliance', 'Dental file transfer security', 'PHI protection', 'Encryption'],
  // Article types want dates and an author; bump dateModified when the copy
  // below changes materially. Left as literals rather than build-time stamps so
  // a rebuild alone never claims the page was revised.
  datePublished: '2026-04-14',
  dateModified: '2026-08-10',
  author: { '@type': 'Organization', name: 'Intake.Dental' },
  publisher: {
    '@type': 'Organization',
    name: 'Intake.Dental',
    url: 'https://intake.dental',
  },
  url: 'https://send.intake.dental/security',
};

export default function SecurityPage() {
  return (
    <div className="landing">
      <JsonLd id="security" data={SECURITY_SCHEMA} />
      <section className="hero">
        <div className="eyebrow">Security &amp; HIPAA</div>
        <h1>Built for dental PHI.<br/>Not retrofitted from a generic file tool.</h1>
        <p className="hero-lead">
          Intake.Dental Send was designed by a practicing dentist around the way real
          offices share patient records — and around the parts of HIPAA that actually
          matter for that workflow.
        </p>
        <TrustBadges />
      </section>

      <section className="content-section">
        <div className="pillar-list">
          {PILLARS.map(p => (
            <div key={p.title} className="pillar">
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>

        <div className="callout">
          <strong>Need our BAA before you start?</strong>
          <p>
            You can read the full Business Associate Agreement at{' '}
            <a href="https://intake.dental/baa" target="_blank" rel="noreferrer">intake.dental/baa</a>.
            You'll be asked to accept it electronically when you verify your email — no
            paperwork required.
          </p>
        </div>
      </section>

      <section className="cta-section">
        <h2>Send a file with confidence</h2>
        <p className="muted">Free for dental practices. BAA in place automatically.</p>
        <Link to="/" className="btn btn-primary btn-large">Get started</Link>
      </section>
    </div>
  );
}
